import type { Maybe, Party, RegisterMutation } from 'generated/gql';
import { DocumentLabel, DocumentType, MutationType } from 'generated/gql';
import { t } from '@lingui/macro';
import type { OnDocumentSaveFn } from 'components/DocumentSidebarForm/DocumentSidebarForm';
import { capitalizeString } from 'helpers/formatShares';
import { uniqBy } from 'lodash';
import type { MutationInputFields } from 'mutations/mutationFields';
import { AdministrationSummaryCards } from 'summaries/AdministrationSummaryCards/AdministrationSummaryCards';
import { AppointDirectorSummaryCards } from 'summaries/AppointDirectorSummaryCards/AppointDirectorSummaryCards';
import { ArticlesOfAssociationSummaryCards } from 'summaries/ArticlesOfAssociationSummaryCards/ArticlesOfAssociationSummaryCards';
import { AttachmentSummaryCards } from 'summaries/AttachmentSummaryCards/AttachmentSummaryCards';
import { ConversionSummaryCards } from 'summaries/ConversionSummaryCards/ConversionSummaryCards';
import { EndAdministrationSummaryCards } from 'summaries/EndAdministrationSummaryCards/EndAdministrationSummaryCards';
import { EndAttachmentSummaryCards } from 'summaries/EndAttachmentSummaryCards/EndAttachmentSummaryCards';
import { EndDirectorshipSummaryCards } from 'summaries/EndDirectorshipSummaryCards/EndDirectorshipSummaryCards';
import { EndPledgeSummaryCards } from 'summaries/EndPledgeSummaryCards/EndPledgeSummaryCards';
import { EndUboSummaryCards } from 'summaries/EndUboSummaryCards/EndUboSummaryCards';
import { EndUsufructSummaryCards } from 'summaries/EndUsufructSummaryCards/EndUsufructSummaryCards';
import { IncorporationSummaryCards } from 'summaries/IncorporationSummaryCards/IncorporationSummaryCards';
import { IssuanceSummaryCards } from 'summaries/IssuanceSummaryCards/IssuanceSummaryCards';
import { LiquidationSummaryCards } from 'summaries/LiquidationSummaryCards/LiquidationSummaryCards';
import { PayingUpSummaryCards } from 'summaries/PayingUpSummaryCards/PayingUpSummaryCards';
import { PledgeSummaryCards } from 'summaries/PledgeSummaryCards/PledgeSummaryCards';
import { TransferSummaryCards } from 'summaries/TransferSummaryCards/TransferSummaryCards';
import { UboSummaryCards } from 'summaries/UboSummaryCards/UboSummaryCards';
import { UsufructSummaryCards } from 'summaries/UsufructSummaryCards/UsufructSummaryCards';
import { WithdrawalSummaryCards } from 'summaries/WithdrawalSummaryCards/WithdrawalSummaryCards';

export interface SummaryCardsComponentProps {
  mutation: RegisterMutation;
  backgroundVariant?: 'white' | 'grey';
  onDocumentSave?: OnDocumentSaveFn;
}
type MutationLabelObject = {
  [type in MutationType]: {
    value: type;
    field: MutationInputFields[type];
    link: string;
    label: () => string;
    documentLabel?: () => string;
    deedLabel?: () => string;
    hasDocumentChoiceStep: (isFoundation: boolean) => boolean;
    isNoDocumentAllowed?: boolean;
    shouldAcknowledge: boolean;
    canChangeAoA?: boolean;
    defaultDocumentType?: DocumentType;
    defaultLabels: (isFoundation: boolean) => DocumentLabel[];
    SummaryCardsComponent: React.FC<SummaryCardsComponentProps>;
  };
};
export const mutationLabelObject: MutationLabelObject = {
  ADMINISTRATION: {
    value: MutationType.Administration,
    field: 'administration',
    link: 'bewind',
    label: () => t`Bewind`,
    deedLabel: () => t`akte van bewind`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || false,
    shouldAcknowledge: true,
    SummaryCardsComponent: AdministrationSummaryCards,
    defaultLabels: () => [],
  },
  APPOINT_DIRECTOR: {
    value: MutationType.AppointDirector,
    field: 'appointDirector',
    link: 'benoeming-bestuurder',
    label: () => t`Bestuurder`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || false,
    isNoDocumentAllowed: true,
    shouldAcknowledge: false,
    SummaryCardsComponent: AppointDirectorSummaryCards,
    defaultLabels: () => [],
  },
  ARTICLE_OF_ASSOCIATION: {
    value: MutationType.ArticleOfAssociation,
    field: 'onboarding',
    link: 'wijziging-statuten',
    label: () => t`Wijziging statuten`,
    deedLabel: () => t`statutenwijziging`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || false,
    shouldAcknowledge: false,
    SummaryCardsComponent: ArticlesOfAssociationSummaryCards,
    defaultDocumentType: DocumentType.NotarialDeed,
    defaultLabels: () => [DocumentLabel.ArticleOfAssociation],
  },
  ATTACHMENT: {
    value: MutationType.Attachment,
    field: 'attachment',
    link: 'beslag',
    label: () => t`Beslag`,
    documentLabel: () => t`beslagexploot`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || false,
    shouldAcknowledge: true,
    defaultDocumentType: DocumentType.PrivateDeed,
    SummaryCardsComponent: AttachmentSummaryCards,
    defaultLabels: () => [],
  },
  CONVERSION: {
    value: MutationType.Conversion,
    field: 'conversion',
    link: 'conversie',
    label: () => t`Conversie`,
    deedLabel: () => t`statutenwijziging`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || true,
    shouldAcknowledge: false,
    canChangeAoA: true,
    SummaryCardsComponent: ConversionSummaryCards,
    defaultLabels: () => [],
  },
  END_ADMINISTRATION: {
    value: MutationType.EndAdministration,
    field: 'endAdministration',
    link: 'beeindiging-bewind',
    label: () => t`Beëindiging bewind`,
    deedLabel: () => t`akte van beëindiging bewind`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || false,
    shouldAcknowledge: true,
    SummaryCardsComponent: EndAdministrationSummaryCards,
    defaultLabels: () => [],
  },
  END_ATTACHMENT: {
    value: MutationType.EndAttachment,
    field: 'endAttachment',
    link: 'beslag-opheffen',
    label: () => t`Beslag opheffen`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || false,
    shouldAcknowledge: true,
    defaultDocumentType: DocumentType.Other,
    SummaryCardsComponent: EndAttachmentSummaryCards,
    defaultLabels: () => [],
  },
  END_DIRECTORSHIP: {
    value: MutationType.EndDirectorship,
    field: 'endDirectorship',
    link: 'beeindiging-bestuurderschap',
    label: () => t`Einde bestuurderschap`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || false,
    shouldAcknowledge: false,
    SummaryCardsComponent: EndDirectorshipSummaryCards,
    defaultLabels: () => [],
  },
  END_PLEDGE: {
    value: MutationType.EndPledge,
    field: 'endPledge',
    link: 'beeindiging-verpanding',
    label: () => t`Beëindiging verpanding`,
    deedLabel: () => t`akte van beëindiging verpanding`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || true,
    shouldAcknowledge: true,
    SummaryCardsComponent: EndPledgeSummaryCards,
    defaultLabels: () => [],
  },
  END_UBO: {
    value: MutationType.EndUbo,
    field: 'endUbo',
    link: 'beeindiging-ubo',
    label: () => t`Einde UBO-registratie`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || false,
    shouldAcknowledge: true,
    SummaryCardsComponent: EndUboSummaryCards,
    defaultLabels: () => [],
  },
  END_USUFRUCT: {
    value: MutationType.EndUsufruct,
    field: 'endUsufruct',
    link: 'beeindiging-vruchtgebruik',
    label: () => t`Beëindiging vruchtgebruik`,
    deedLabel: () => t`akte van beëindiging vruchtgebruik`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || true,
    shouldAcknowledge: true,
    SummaryCardsComponent: EndUsufructSummaryCards,
    defaultLabels: () => [],
  },
  INCORPORATION: {
    value: MutationType.Incorporation,
    field: 'incorporation',
    link: 'initiele-uitgifte',
    label: () => t`Initiële uitgifte`,
    deedLabel: () => t`akte van oprichting`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || false,
    shouldAcknowledge: false,
    canChangeAoA: true,
    SummaryCardsComponent: IncorporationSummaryCards,
    defaultLabels: (isFoundation) =>
      isFoundation
        ? [DocumentLabel.ArticleOfAssociation, DocumentLabel.TrustConditions]
        : [DocumentLabel.ArticleOfAssociation],
  },
  ISSUANCE: {
    value: MutationType.Issuance,
    field: 'issuance',
    link: 'uitgifte',
    label: () => t`Uitgifte`,
    deedLabel: () => t`akte van uitgifte`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || false,
    shouldAcknowledge: false,
    canChangeAoA: true,
    SummaryCardsComponent: IssuanceSummaryCards,
    defaultLabels: () => [],
  },
  LIQUIDATION: {
    value: MutationType.Liquidation,
    field: 'liquidation',
    link: 'beeindiging-register',
    label: () => t`Beëindiging register`,
    deedLabel: () => t`akte van ontbinding`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || true,
    shouldAcknowledge: false,
    SummaryCardsComponent: LiquidationSummaryCards,
    defaultLabels: () => [],
  },
  ONBOARDING: {
    value: MutationType.Onboarding,
    field: 'onboarding',
    link: 'bestaand-register',
    label: () => t`Registratie van bestaand register`,
    documentLabel: () => t`digitale kopie van het bestaande register`,
    hasDocumentChoiceStep: () => false,
    shouldAcknowledge: false,
    defaultDocumentType: DocumentType.Other,
    SummaryCardsComponent: IncorporationSummaryCards,
    defaultLabels: () => [],
  },
  PAYING_UP: {
    value: MutationType.PayingUp,
    field: 'payingUp',
    link: 'volstorting',
    label: () => t`Volstorting`,
    deedLabel: () => t`akte van volstorting`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || true,
    isNoDocumentAllowed: true,
    shouldAcknowledge: true,
    SummaryCardsComponent: PayingUpSummaryCards,
    defaultLabels: () => [],
  },
  PLEDGE: {
    value: MutationType.Pledge,
    field: 'pledge',
    link: 'verpanding',
    label: () => t`Verpanding`,
    deedLabel: () => t`akte van verpanding`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || true,
    shouldAcknowledge: true,
    SummaryCardsComponent: PledgeSummaryCards,
    defaultLabels: () => [],
  },
  TRANSFER: {
    value: MutationType.Transfer,
    field: 'transfer',
    link: 'overdracht',
    label: () => t`Overdracht`,
    deedLabel: () => t`akte van overdracht`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || false,
    shouldAcknowledge: true,
    SummaryCardsComponent: TransferSummaryCards,
    defaultLabels: () => [],
  },
  TRUST_CONDITIONS: {
    value: MutationType.TrustConditions,
    field: 'onboarding',
    link: 'wijziging-administratievoorwaarden',
    label: () => t`Wijziging administratievoorwaarden`,
    deedLabel: () => t`Wijziging administratievoorwaarden`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || true,
    shouldAcknowledge: false,
    SummaryCardsComponent: ArticlesOfAssociationSummaryCards,
    defaultDocumentType: DocumentType.NotarialDeed,
    defaultLabels: () => [DocumentLabel.TrustConditions],
  },
  UBO: {
    value: MutationType.Ubo,
    field: 'ubo',
    link: 'ubo',
    label: () => t`UBO registreren`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || false,
    shouldAcknowledge: true,
    SummaryCardsComponent: UboSummaryCards,
    defaultLabels: () => [],
  },
  USUFRUCT: {
    value: MutationType.Usufruct,
    field: 'usufruct',
    link: 'vruchtgebruik',
    label: () => t`Vruchtgebruik`,
    deedLabel: () => t`akte van vruchtgebruik`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || true,
    shouldAcknowledge: true,
    SummaryCardsComponent: UsufructSummaryCards,
    defaultLabels: () => [],
  },
  WITHDRAWAL: {
    value: MutationType.Withdrawal,
    field: 'withdrawal',
    link: 'intrekking',
    label: () => t`Intrekking`,
    deedLabel: () => t`akte van intrekking`,
    hasDocumentChoiceStep: (isFoundation) => isFoundation || true,
    shouldAcknowledge: true,
    canChangeAoA: true,
    SummaryCardsComponent: WithdrawalSummaryCards,
    defaultLabels: () => [],
  },
};

export const formatMutationType = ({ type, title }: Pick<RegisterMutation, 'type' | 'title'>) => {
  const mutationLabel = mutationLabelObject[type];
  return mutationLabel.label() + (title ? `: ${title}` : '');
};

export const formatMutationTypeLink = (type: MutationType) => {
  const mutationLabel = mutationLabelObject[type];
  return mutationLabel.link;
};

const PPU_ROLLBACK_PREFIX = 'rollback:';

const normalizePpuCode = (value: string) => value.toLowerCase().replace(/[\s_-]/g, '');

// PPU descriptions arrive as MutationType values without separators: APPOINT_DIRECTOR -> appointdirector
const ppuDescriptionTypes = new Map<string, MutationType>(
  Object.values(MutationType).map((type) => [normalizePpuCode(type), type]),
);

export const formatPpuDescription = (description?: Maybe<string>) => {
  const trimmed = description?.trim();
  if (!trimmed) return '';

  const isRollback = trimmed.toLowerCase().startsWith(PPU_ROLLBACK_PREFIX);
  const code = (isRollback ? trimmed.slice(PPU_ROLLBACK_PREFIX.length) : trimmed).trim();

  const type = ppuDescriptionTypes.get(normalizePpuCode(code));
  const label = type ? mutationLabelObject[type].label() : capitalizeString(code);

  return isRollback ? t`Terugdraaien: ${label}` : label;
};

export const getUniquePartiesFromMutation = (mutation: RegisterMutation): Party[] => {
  const mutationLabel = mutationLabelObject[mutation.type];
  if (mutationLabel.field === 'liquidation') return [];

  const field = mutationLabel.field;
  const mutationObjects: Maybe<any[]> | undefined = mutation[field];
  if (!mutationObjects) return [];
  return uniqBy(
    mutationObjects.reduce((parties: Party[], mutationObject) => {
      const { to, from, owner, director } = mutationObject;
      const delegatee = mutationObject.details?.delegatee;
      const administrator = mutationObject.details?.administrator;
      const bailiff = mutationObject.details?.bailiff;
      const requiredBy = mutationObject.details?.requiredBy;
      const allParties = [
        ...parties,
        ...[to, from, owner, director, delegatee, administrator, bailiff].filter((x) => x),
      ];
      if (requiredBy) return [...allParties, ...requiredBy];
      return allParties;
    }, []) as Party[],
    'id',
  );
};
